import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import React, { useState } from "react";
import { editReceivingInvestigationFile2 } from "services/prosecutorPoliceService";
import { InvestigationFile } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

interface Props {
  file: InvestigationFile;
  onClose: (refetch: boolean) => void;
}

const ResumeInvestigation: React.FC<Props> = ({ file, onClose }) => {
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = React.useState(false);

  const [formState, setFormState] = useState<{
    id?: number;
    case_resume_date?: Date;
    case_resume_reason?: string;
    decision_file?: File | null;
  }>({});

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState?.case_resume_reason) return "Resume Reason is Required.";
    else if (!formState?.case_resume_date) return "Resume Date is Required.";
    else if (!formState?.decision_file)
      return "Resume Decision File is Required.";
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    // Resume is now a prosecutor decision pending coordinator approval — the
    // case stays suspended until the coordinator approves. (No "resume" choice
    // exists for prosecutor_decision_type, so the pending state is identified
    // by is_case_suspended === true + prosecutor_decision_status "pending".)
    editReceivingInvestigationFile2({
      id: file?.id ?? "",
      case_resume_date: formState?.case_resume_date,
      case_resume_reason: formState?.case_resume_reason,
      decision_file: formState?.decision_file || undefined,
      prosecutor_decision_status: "pending",
    })
      .then(() => {
        showTooltip("Successfully Submitted!", "success");
        onClose(true);
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-[600px]">
        <h2 className="text-lg font-semibold text-gray-800">
          Resume Investigation
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="rounded-lg  mt-4 ">
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Resume Reason
              </label>
              <textarea
                placeholder="Reason ..."
                rows={4}
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="case_resume_reason"
                value={formState?.case_resume_reason ?? ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Resume Date
              </label>
              <input
                type="date"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="case_resume_date"
                value={
                  formState?.case_resume_date
                    ? new Date(formState.case_resume_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4">
              <FileUpload
                label="Resume Decision File"
                fileTypes="*"
                required={true}
                onChange={(uploaded) =>
                  setFormState((prev) => ({
                    ...prev,
                    decision_file: uploaded,
                  }))
                }
              />
            </div>
          </div>
          <div className="mt-4 flex justify-between gap-2">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded w-full hover:bg-gray-300"
              onClick={() => onClose(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`border border-[#E90101] bg-[#E90101] px-10 py-2 text-white 
                        font-[Poppins] cursor-pointer font-[500] w-full
                        text-base rounded-md hover:bg-white hover:text-[#E90101]
                        ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                "Resume"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResumeInvestigation;
